import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  showPopup,
  closePopup,
  closeAllPopups,
  updatePopup,
  PopupConfig,
  PopupType,
} from "../redux/slices/popupSlice/popupSlice";

export const usePopup = () => {
  const dispatch = useAppDispatch();
  const currentPopup = useAppSelector((state) => state.popup.currentPopup);
  const popups = useAppSelector((state) => state.popup.popups);

  const show = useCallback(
    (config: PopupConfig | string, message?: string) => {
      let popupConfig: PopupConfig;

      // Quick show with just message
      if (typeof config === "string") {
        popupConfig = {
          type: PopupType.INFO,
          title: "",
          message: config,
        };
      } else {
        popupConfig = config;
      }

      dispatch(showPopup(popupConfig));
    },
    [dispatch]
  );

  const close = useCallback(
    (id?: string) => {
      dispatch(closePopup(id));
    },
    [dispatch]
  );

  const closeAll = useCallback(() => {
    dispatch(closeAllPopups());
  }, [dispatch]);

  const update = useCallback(
    (id: string, config: Partial<PopupConfig>) => {
      dispatch(updatePopup({ id, config }));
    },
    [dispatch]
  );

  // Convenience methods for different popup types
  const showInfo = useCallback(
    (title: string, message: string, config?: Partial<PopupConfig>) => {
      show({
        type: PopupType.INFO,
        title,
        message,
        ...config,
      });
    },
    [show]
  );

  const showSuccess = useCallback(
    (title: string, message: string, config?: Partial<PopupConfig>) => {
      show({
        type: PopupType.SUCCESS,
        title,
        message,
        ...config,
      });
    },
    [show]
  );

  const showWarning = useCallback(
    (title: string, message: string, config?: Partial<PopupConfig>) => {
      show({
        type: PopupType.WARNING,
        title,
        message,
        ...config,
      });
    },
    [show]
  );

  const showError = useCallback(
    (title: string, message: string, config?: Partial<PopupConfig>) => {
      show({
        type: PopupType.ERROR,
        title,
        message,
        ...config,
      });
    },
    [show]
  );

  const showConfirm = useCallback(
    (
      title: string,
      message: string,
      onConfirm: () => void,
      onCancel?: () => void,
      config?: Partial<PopupConfig>
    ) => {
      show({
        type: PopupType.CONFIRM,
        title,
        message,
        onConfirm,
        onCancel,
        buttons: [
          {
            text: config?.buttons?.[0]?.text || "ביטול",
            onClick: onCancel,
            type: "default",
          },
          {
            text: config?.buttons?.[1]?.text || "אישור",
            onClick: onConfirm,
            type: "primary",
          },
        ],
        ...config,
      });
    },
    [show]
  );

  return {
    show,
    showInfo,
    showSuccess,
    showWarning,
    showError,
    showConfirm,
    close,
    closeAll,
    update,
    currentPopup,
    popups,
    hasPopups: popups.length > 0,
  };
};

